import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, MessageCircle } from "lucide-react";

/**
 * WhatsAppLoteSummary — resumo pré-salvamento do lote: quantos números
 * têm WhatsApp válido vs quantos não. Só aparece se a empresa tem Z-API
 * configurada (senão silencia).
 *
 * Faz N requests em paralelo (limitado internamente pelo browser). Debounce
 * simples via `rows` prop — a cada mudança, agenda revalidação em 800ms.
 */
export function WhatsAppLoteSummary({ rows }) {
  const [state, setState] = useState({ configured: null, has: 0, no: 0, checked: 0, total: 0 });

  useEffect(() => {
    const validPhones = (rows || [])
      .map((r) => String(r.cliente_telefone || "").replace(/\D/g, ""))
      .filter((d) => d.length >= 10);
    const total = validPhones.length;
    if (total === 0) {
      setState({ configured: null, has: 0, no: 0, checked: 0, total: 0 });
      return;
    }
    const t = setTimeout(async () => {
      // Detecta se a empresa tem Z-API configurada primeiro (com 1 request)
      let configured = null;
      let has = 0, no = 0, checked = 0;
      try {
        const first = await api.get(`/whatsapp/check-phone?tel=${encodeURIComponent(validPhones[0])}`);
        if (!first.data?.configured) {
          setState({ configured: false, has: 0, no: 0, checked: 0, total });
          return;
        }
        configured = true;
        if (first.data?.has_whatsapp === true) has++;
        else if (first.data?.has_whatsapp === false) no++;
        checked++;
        // Consulta os demais em paralelo — sem afetar tela
        const rest = await Promise.all(validPhones.slice(1).map(async (p) => {
          try {
            const r = await api.get(`/whatsapp/check-phone?tel=${encodeURIComponent(p)}`);
            return r.data?.has_whatsapp;
          } catch { return null; }
        }));
        for (const r of rest) {
          checked++;
          if (r === true) has++;
          else if (r === false) no++;
        }
      } catch {
        configured = false;
      }
      setState({ configured, has, no, checked, total });
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify((rows || []).map((r) => r.cliente_telefone || ""))]);

  if (state.configured === null || state.total === 0) return null;
  if (state.configured === false) return null; // empresa sem Z-API — não polui

  const loading = state.checked < state.total;
  return (
    <div
      className="text-xs px-3 py-2 rounded border bg-emerald-50 border-emerald-200 flex items-center gap-3"
      data-testid="lote-wa-summary"
    >
      <MessageCircle className="w-4 h-4 text-emerald-700 shrink-0" />
      <span className="font-semibold text-emerald-900">Validação WhatsApp:</span>
      <span className="flex items-center gap-1 text-emerald-700">
        <CheckCircle2 className="w-3 h-3" /> <b data-testid="lote-wa-has">{state.has}</b> com
      </span>
      <span className="flex items-center gap-1 text-rose-700">
        <XCircle className="w-3 h-3" /> <b data-testid="lote-wa-no">{state.no}</b> sem
      </span>
      {loading && (
        <span className="flex items-center gap-1 text-slate-500">
          <Loader2 className="w-3 h-3 animate-spin" /> validando {state.checked}/{state.total}…
        </span>
      )}
      {state.no > 0 && !loading && (
        <span className="text-rose-700 italic ml-auto">
          {state.no} sem WhatsApp — mensagem automática não será enviada
        </span>
      )}
    </div>
  );
}
